import {
  Box,
  Button,
  Container,
  Divider,
  FormLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Months } from "../../../../utils";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import Loading from "./skeletonLoading/Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import { useTranslation } from "react-i18next";
import EvaluationList from "./EvaluationList";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1; // Month is 0-indexed
const years = Array.from(
  { length: currentYear - 2020 + 1 },
  (v, i) => 2020 + i,
);

const Evaluation = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [formData, setFormData] = useState({
    employee: "",
    month: currentMonth,
    year: currentYear,
    description: "",
    rate: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const { data: employeeData } = useFetchObjects(["users"], "users", token);

  const { handleAdd, resetForm, loading, isError, isLoading } = useAdd(
    "employee-evaluations",
    token,
    "Evaluation submitted successfully!",
    "Failed to submit evaluation",
  );

  useEffect(() => {
    if (resetForm) {
      setFormData({
        employee: "",
        month: currentMonth,
        year: currentYear,
        description: "",
        rate: 0,
      });
      setFormErrors({});
      setSuccessMessage("");
      setErrorMessage("");
    }
  }, [resetForm]);

  const handleSave = async (e) => {
    e.preventDefault();
    let errors = {};

    if (!formData.employee) {
      errors.employee = t("hr.employeeRequired");
    }
    if (!formData.month) {
      errors.month = t("hr.monthRequired");
    }
    if (!formData.year) {
      errors.year = t("hr.yearRequired");
    }
    if (!formData.description) {
      errors.description = t("hr.descriptionRequired");
    }
    if (formData.rate < 1 || formData.rate > 10) {
      errors.rate = t("hr.rate");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      const success = handleAdd(formData);
      if (success) {
        setFormData({
          employee: "",
          month: currentMonth,
          year: currentYear,
          description: "",
          rate: 0,
        });
        setFormErrors({});
      } else {
        setFormErrors(errors);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  if (isLoading) return <Loading />;
  if (isError)
    return (
      <Grid container>
        <ErrorPage />
      </Grid>
    );

  return (
    <Container>
      <Paper
        sx={{
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          marginTop: "2rem",
          marginBottom: "2rem",
          padding: "2rem",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={600} variant="h4" color="primary">
            {t("hr.evaluationForm")}
          </Typography>
          <Button variant="contained" color="secondary">
            {t("hr.reports")}
          </Button>
        </Stack>
        <Divider sx={{ mt: 2, mb: 2 }} />

        {successMessage && (
          <Typography color="green" variant="body1" gutterBottom>
            {successMessage}
          </Typography>
        )}
        {errorMessage && (
          <Typography color="red" variant="body1" gutterBottom>
            {errorMessage}
          </Typography>
        )}

        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            {/* Employee Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.employee")}</FormLabel>
              </Box>
              <Autocomplete
                options={employeeData}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}`
                }
                onChange={(event, value) => {
                  setFormData({ ...formData, employee: value?.id || "" });
                  setFormErrors({ ...formErrors, employee: "" });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    fullWidth
                    label={t("hr.selectEmployee")}
                    error={!!formErrors.employee}
                    helperText={formErrors.employee}
                  />
                )}
              />
            </Grid>

            {/* Month Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.month")}</FormLabel>
              </Box>
              <TextField
                select
                size="small"
                name="month"
                value={formData.month}
                label={t("hr.selectMonth")}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                error={!!formErrors.month}
                helperText={formErrors.month}
              >
                {Months.map((monthOption) => (
                  <MenuItem key={monthOption.id} value={monthOption.id}>
                    {monthOption.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Year Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.year")}</FormLabel>
              </Box>
              <TextField
                select
                size="small"
                name="year"
                value={formData.year}
                label={t("hr.selectYear")}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                error={!!formErrors.year}
                helperText={formErrors.year}
              >
                {years.map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption}>
                    {yearOption}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Description Field */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.description1")}</FormLabel>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
                error={!!formErrors.description}
                helperText={formErrors.description}
              />
            </Grid>

            {/* Rate Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.evaluation")}</FormLabel>
              </Box>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                fullWidth
                name="rate"
                value={formData.rate}
                onChange={handleChange}
                inputProps={{ min: 1, max: 10 }}
                error={!!formErrors.rate}
                helperText={formErrors.rate || t("hr.numberBetween")}
              />
            </Grid>
          </Grid>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button variant="contained" type="submit" disabled={loading}>
              {t("hr.save")}
            </Button>
          </Stack>
        </form>

        <Box mt={3}>
          <Divider />
          <EvaluationList />
        </Box>
      </Paper>
    </Container>
  );
};

export default Evaluation;
